<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

const { t } = useI18n();
const { localeTag } = useDateLocale();

const props = defineProps({
  funnel: {
    type: Object,
    default: () => ({ sent: 0, delivered: 0, read: 0, failed: 0 }),
  },
});

// Funnel values arrive cumulative: a `read` message counts in `sent` and
// `delivered` too. The denominator is the total attempted (sent + failed) so
// each bar's percentage tells how much of the audience reached that stage.
const attempted = computed(() => {
  const { sent, failed } = props.funnel;
  return (sent || 0) + (failed || 0);
});

const rows = computed(() => {
  const total = attempted.value;
  return [
    {
      key: 'sent',
      label: t('FLOW_BUILDER.REPORTS.FUNNEL.SENT'),
      count: props.funnel.sent || 0,
      color: 'bg-n-blue-9',
    },
    {
      key: 'delivered',
      label: t('FLOW_BUILDER.REPORTS.FUNNEL.DELIVERED'),
      count: props.funnel.delivered || 0,
      color: 'bg-n-teal-9',
    },
    {
      key: 'read',
      label: t('FLOW_BUILDER.REPORTS.FUNNEL.READ'),
      count: props.funnel.read || 0,
      color: 'bg-n-amber-9',
    },
    {
      key: 'failed',
      label: t('FLOW_BUILDER.REPORTS.FUNNEL.FAILED'),
      count: props.funnel.failed || 0,
      color: 'bg-n-ruby-9',
    },
  ].map(r => ({
    ...r,
    pct: total > 0 ? Math.round((r.count / total) * 100) : 0,
    width: total > 0 ? `${(r.count / total) * 100}%` : '0%',
  }));
});

const numberFormatter = computed(() => new Intl.NumberFormat(localeTag.value));
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-for="row in rows" :key="row.key" class="flex items-center gap-3">
      <span class="w-24 text-sm text-n-slate-11 shrink-0">{{ row.label }}</span>
      <div class="flex-1 h-8 bg-n-alpha-1 rounded-lg overflow-hidden relative">
        <div
          class="h-full rounded-lg transition-all duration-300"
          :class="[row.color]"
          :style="{ width: row.width }"
        />
        <div
          class="absolute inset-0 flex items-center px-3 text-xs font-medium text-n-slate-12"
        >
          <span class="tabular-nums">{{
            numberFormatter.format(row.count)
          }}</span>
          <span class="ml-auto text-n-slate-11 tabular-nums"
            >{{ `${row.pct}%` }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>
